import type { TooltipProps, TooltipProviderProps } from '@radix-ui/react-tooltip';
import { Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import type { ComponentProps, FunctionComponent } from 'react';
import { createContext, useContext } from 'react';

const DEFAULT_DELAY_DURATION = 200;

// After one tooltip closes, the next one within this window opens without the
// delay, so moving along a row of controls does not wait at every stop.
const DEFAULT_SKIP_DELAY_DURATION = 300;

// True below a TooltipProvider, so a Tooltip reuses it instead of creating its
// own; a provider per tooltip would never carry the skip-delay across them.
const TooltipProviderPresenceContext = createContext(false);

const TooltipProvider: FunctionComponent<TooltipProviderProps> = ({
    children,
    delayDuration = DEFAULT_DELAY_DURATION,
    skipDelayDuration = DEFAULT_SKIP_DELAY_DURATION,
    ...props
}) => (
    <Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration} {...props}>
        <TooltipProviderPresenceContext.Provider value={true}>{children}</TooltipProviderPresenceContext.Provider>
    </Provider>
);

const TooltipContent: FunctionComponent<ComponentProps<typeof Content>> = ({ className, ...props }) => (
    <Content
        className={clsx(
            'bg-surface-inverse rounded-sm shadow-md text-text-on-inverse text-meta font-sans font-normal max-w-[300px] px-s py-xs z-[99999]',
            className,
        )}
        {...props}
    />
);

export const Tooltip: FunctionComponent<TooltipProps> & {
    Content: typeof TooltipContent;
    Portal: typeof Portal;
    Provider: typeof TooltipProvider;
    Root: typeof Root;
    Trigger: typeof Trigger;
} = ({ children, delayDuration, ...props }) => {
    const hasProvider = useContext(TooltipProviderPresenceContext);

    // Under a shared provider, its delay applies unless this tooltip sets one.
    if (hasProvider) {
        return (
            <Root {...(delayDuration === undefined ? {} : { delayDuration })} {...props}>
                {children}
            </Root>
        );
    }

    return (
        <TooltipProvider delayDuration={delayDuration ?? DEFAULT_DELAY_DURATION}>
            <Root {...props}>{children}</Root>
        </TooltipProvider>
    );
};

Tooltip.Provider = TooltipProvider;
Tooltip.Root = Root;
Tooltip.Trigger = Trigger;
Tooltip.Content = TooltipContent;
Tooltip.Portal = Portal;
